// Row geometry for the nav chrome, in one place so links, inert stubs and the
// sign-out button are the same shape.
//
// This is where "responsive" actually lives. `panel` is the no-choice-yet
// state: the labelled nav, because a viewer who has never touched the control
// should see where everything is rather than a column of unlabelled glyphs.
// Collapsing to the rail is opted into, and `panel-collapsed` / `panel-expanded`
// are that choice, applying at every width from `navtab` up. Below `navtab`
// none of this renders — that width gets the hamburger and the drawer instead.

import type { NavCollapseState } from './navCollapse';

/** How a nav row lays out. */
export type NavItemVariant = 'panel' | 'panel-collapsed' | 'panel-expanded' | 'drawer';

/** The subset the left panel can be in — everything but the mobile drawer. */
export type PanelVariant = Exclude<NavItemVariant, 'drawer'>;

/** The row variant for a panel in the given collapse state. */
export function panelVariant(state: NavCollapseState): PanelVariant {
  if (state === 'collapsed') return 'panel-collapsed';
  if (state === 'expanded') return 'panel-expanded';
  return 'panel';
}

const ROW: Record<NavItemVariant, string> = {
  panel: 'h-10 w-full justify-start gap-3 px-3',
  'panel-collapsed': 'h-10 w-10 justify-center',
  'panel-expanded': 'h-10 w-full justify-start gap-3 px-3',
  drawer: 'h-11 w-full justify-start gap-3 px-3',
};

export const LABEL_CLASS: Record<NavItemVariant, string> = {
  panel: 'truncate',
  'panel-collapsed': 'hidden',
  'panel-expanded': 'truncate',
  drawer: 'truncate',
};

/** Panel width for a collapse state. Mirrors LABEL_CLASS: the panel is only
 *  wide where the label is visible. */
export const PANEL_WIDTH: Record<PanelVariant, string> = {
  panel: 'w-[264px] items-stretch px-3',
  'panel-collapsed': 'w-16 items-center',
  'panel-expanded': 'w-[264px] items-stretch px-3',
};

/** Alignment for the panel's footer cluster, matching PANEL_WIDTH. */
export const FOOTER_ALIGN: Record<PanelVariant, string> = {
  panel: 'items-stretch',
  'panel-collapsed': 'items-center',
  'panel-expanded': 'items-stretch',
};

/** The full class string for a nav row of the given variant. */
export function navRowClass(variant: NavItemVariant): string {
  return `relative flex shrink-0 items-center rounded-[10px] border-0 bg-transparent text-sm font-medium transition-colors ${ROW[variant]}`;
}

/** Whether a block belongs to the icon-only band. Mirrors LABEL_CLASS: with no
 *  stored choice the panel is labelled, so the icon band is off until the
 *  viewer collapses it. */
export const COLLAPSED_ONLY: Record<PanelVariant, string> = {
  panel: 'hidden',
  'panel-collapsed': 'flex',
  'panel-expanded': 'hidden',
};

/** The complement of COLLAPSED_ONLY, for blocks only the labelled panel has
 *  room for — the section tree and the account rows. */
export const EXPANDED_ONLY: Record<PanelVariant, string> = {
  panel: 'flex',
  'panel-collapsed': 'hidden',
  'panel-expanded': 'flex',
};
